import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Chip,
  Image,
  Spinner,
  addToast,
} from '@heroui/react';
import { useTranslation } from 'react-i18next';
import {
  FaArrowLeft,
  FaCalendarCheck,
  FaCalendarDay,
  FaCircleCheck,
} from 'react-icons/fa6';
import {
  useFetchMyPackages,
  useFetchServiceDataById,
} from '../../../hook/useQueryData';
import { useBookSession } from '../../../hook/useMutationData';
import { getBookingById } from '../../../service/bookingService';
import DateAndStaff from '../Booking/DateAndStaff';
import CustomButton from '../../components/CustomButton';
import CustomAlert from '../../components/CustomAlert';
import SmallLoadingSpinner from '../../components/common/ui/SmallLoadingSpinner';
import BookingCheckoutModal from '../../components/common/ui/BookingCheckoutModal';
import { CustomerPackage, ServiceGet } from '../../../utils/type';
import { useAuth } from '../../../context/AuthContext';
import { all_routes } from '../../../core/data/routes/all_routes';
import { ANY_STAFF_ID, ANY_STAFF_LABEL } from '../../../utils/common';
import fallbackImage from '../../../../public/fallback-image.png';
import AddOnSelector, {
  getAddOnId,
  getAddOnName,
} from '../../components/AddOnSelector';
import PaymentSection, {
  STRIPE_PUBLISHABLE_KEY,
} from '../Booking/PaymentSection';

// Same shape MyPackages.tsx uses (formatDate is kept local there too — a
// shared date-format util is a follow-up DRY refactor, not part of this pass).
const formatDate = (value?: string): string => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

interface SlotSelection {
  date: string;
  timeSlotId: string;
  staffId?: string;
  time?: string;
  staff?: { fullName?: string };
  start?: string;
  end?: string;
}

/**
 * Phase 3 — Book a Session (credit redemption).
 * Route: /service/credit-session/:serviceId
 *
 * Reuses the DateAndStaff slot picker. The customer's matching CustomerPackage
 * (with sessions remaining) funds the booking; confirming calls
 * POST /v1/customer-package/:id/book-session. No payment for the package
 * itself — credits only.
 *
 * Phase 3.1 — additional services split into two groups relative to the
 * SPECIFIC session number about to be booked (sessionsTotal - sessionsRemaining
 * + 1, mirrored server-side in customerPackageService.getUpcomingSessionNumber
 * so the two never disagree):
 *   - "Included in Your Package": add-ons the customer already paid for at
 *     purchase/offer time, specifically assigned to this session number (see
 *     PackageSelection's SessionAddOnSelector). Rendered read-only — nothing
 *     to select, no extra charge.
 *   - "Optional Add-ons": every other add-on the service offers. Selecting
 *     these here is a brand-new, separately-charged purchase (unchanged
 *     from the pre-3.1 flow) — a checkout modal confirms the charge before
 *     booking; with none selected, booking proceeds immediately.
 * Only the EXTRA selection is ever sent as `additionalServiceIds` to
 * bookSession — the backend derives the included set itself from the
 * package's own sessionAddOns rather than trusting the client to resend it.
 */
const CreditSession = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const bookSessionMutation = useBookSession();
  const { user } = useAuth();

  const { data, isLoading: serviceLoading } =
    useFetchServiceDataById(serviceId);
  const service: ServiceGet | null = useMemo(
    () =>
      data?.serviceInfo || data?.data?.serviceInfo || data?.serviceInfo || null,
    [data]
  );

  const { data: packages = [], isLoading: packagesLoading } =
    useFetchMyPackages();
  const matchingPackage: CustomerPackage | undefined = useMemo(() => {
    // Try to match by service.serviceId (counter-based ID) first,
    // then by service.displayId, then by the URL param as-is
    // (which might be either format, since backend accepts both).
    const serviceIdToMatch = service?.serviceId;
    const serviceDisplayIdToMatch = service?.displayId;
    return (
      packages.find(
        (pkg) => pkg.serviceId === serviceIdToMatch && pkg.sessionsRemaining > 0
      ) ||
      packages.find(
        (pkg) =>
          pkg.service?.displayId === serviceDisplayIdToMatch &&
          pkg.sessionsRemaining > 0
      ) ||
      packages.find(
        (pkg) => pkg.serviceId === serviceId && pkg.sessionsRemaining > 0
      ) ||
      packages.find((pkg) => pkg.sessionsRemaining > 0)
    );
  }, [packages, serviceId, service]);

  // The 1-based session number this booking will redeem. Prefer the
  // backend-computed value (customerPackageService.getUpcomingSessionNumber,
  // surfaced on every getMyPackages item) so this page never disagrees with
  // the server about which session is "next"; fall back to the same formula
  // for packages fetched before that field existed.
  const currentSessionNumber = useMemo(() => {
    if (!matchingPackage) return 1;
    return (
      matchingPackage.currentSessionNumber ??
      (matchingPackage.sessionsTotal || 0) -
        (matchingPackage.sessionsRemaining || 0) +
        1
    );
  }, [matchingPackage]);

  // Additional-service ids already bundled (paid for) into THIS session
  // number. Prefer the backend-computed list; fall back to deriving it from
  // the package's own sessionAddOns for older responses.
  const includedAddOnIds = useMemo(() => {
    if (!matchingPackage) return [];
    if (matchingPackage.includedAddOnIdsForNextSession) {
      return matchingPackage.includedAddOnIdsForNextSession;
    }
    return (matchingPackage.sessionAddOns || [])
      .filter((entry) =>
        (entry.sessionNumbers || []).includes(currentSessionNumber)
      )
      .map((entry) => entry.additionalServiceId);
  }, [matchingPackage, currentSessionNumber]);

  // Every addon on the service, tagged with a STABLE id computed once here
  // (rather than re-derived per-array-position after splitting below) so an
  // addon with neither `additionalServiceId` nor `id` doesn't silently
  // change identity (getAddOnId's `addon_${index}` fallback) once it moves
  // into a shorter, re-indexed included/extra list.
  const allAddOnsWithId = useMemo(
    () =>
      (service?.additionalServices || []).map((addon, index) => ({
        ...addon,
        id: getAddOnId(addon, index),
      })),
    [service?.additionalServices]
  );

  const includedAddOns = useMemo(
    () =>
      allAddOnsWithId.filter((addon) => includedAddOnIds.includes(addon.id)),
    [allAddOnsWithId, includedAddOnIds]
  );
  const extraAddOns = useMemo(
    () =>
      allAddOnsWithId.filter((addon) => !includedAddOnIds.includes(addon.id)),
    [allAddOnsWithId, includedAddOnIds]
  );

  const [slot, setSlot] = useState<SlotSelection | null>(null);
  const [dateAndStaffState, setDateAndStaffState] = useState<any>(null);
  // Only ever holds ids from `extraAddOns` — the picker rendered for these
  // (below) is built from that list, so there's no path for an included id
  // to land here.
  const [selectedAdditionalServiceIds, setSelectedAdditionalServiceIds] =
    useState<string[]>([]);
  const [showBookingCheckout, setShowBookingCheckout] = useState(false);

  // Phase 4 — appointment-time add-on payment. Once bookSession succeeds, if
  // the created booking has a chargeable addonSubtotal (extras selected
  // above), this drives a post-booking Stripe payment step before handing
  // off to the success toast + navigation. 'idle' | 'paying' | 'confirming'
  // | 'done'.
  const [addonPaymentPhase, setAddonPaymentPhase] = useState<
    'idle' | 'paying' | 'confirming' | 'done'
  >('idle');
  const [createdBooking, setCreatedBooking] = useState<any>(null);
  const [addonPaymentError, setAddonPaymentError] = useState('');
  const addonPollCancelRef = useRef(false);
  const onlineAddonPaymentAvailable = Boolean(STRIPE_PUBLISHABLE_KEY);

  useEffect(
    () => () => {
      addonPollCancelRef.current = true;
    },
    []
  );

  const isLoading = serviceLoading || packagesLoading;

  // Only the EXTRA add-ons the customer actually selected, resolved to
  // {id, name, price} for the checkout modal — included ones never appear
  // here since they're not chargeable.
  const selectedAddOnDetails = useMemo(() => {
    return extraAddOns
      .map((addon, index) => ({
        id: getAddOnId(addon, index),
        name: getAddOnName(addon, index),
        price: Number(addon.price || 0),
      }))
      .filter((addon) => selectedAdditionalServiceIds.includes(addon.id));
  }, [extraAddOns, selectedAdditionalServiceIds]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-24">
        <Spinner label="Loading…" />
      </div>
    );
  }

  if (!service) {
    return (
      <div className="p-6">
        <Card className="border border-gray-200">
          <CardBody className="py-12 text-center text-body text-gray-600">
            {t('credit.service_error', 'We could not load this service.')}
          </CardBody>
        </Card>
      </div>
    );
  }

  if (!matchingPackage) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12">
        <Card className="border border-gray-200">
          <CardBody className="py-12 text-center space-y-3">
            <p className="text-body font-medium text-gray-700">
              {t('credit.no_credits_title', 'No available appointments')}
            </p>
            <p className="text-caption text-gray-500 max-w-sm mx-auto">
              {t(
                'credit.no_credits_hint',
                'You need a package with remaining appointments to book an appointment for this service.'
              )}
            </p>
            <div className="flex justify-center gap-2 flex-wrap pt-2">
              <CustomButton
                label={t('credit.view_packages', 'View My Packages')}
                color="primary"
                variant="flat"
                size="sm"
                onPress={() => navigate(all_routes.customerPackages)}
              />
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  const showBookedToastAndNavigate = () => {
    addToast({
      title: t('credit.booking_success_title', 'Appointment Booked!'),
      description: t(
        'credit.booking_success_desc',
        'Your appointment has been confirmed. 1 appointment has been deducted from your package.'
      ),
      radius: 'md',
      color: 'success',
    });
    requestAnimationFrame(() => {
      setTimeout(() => {
        navigate(all_routes.customerPackages);
      }, 0);
    });
  };

  // Poll GET /booking/:id until the addon PaymentIntent's capture webhook
  // flips addonPaymentStatus to 'PAID' — mirrors PackageCheckout.tsx's
  // pollForPaymentResolution, scoped to one booking's addon fields.
  const pollForAddonPaymentResolution = (bookingId: string) => {
    const startedAt = Date.now();
    addonPollCancelRef.current = false;

    const pollOnce = async () => {
      if (addonPollCancelRef.current) return;
      try {
        const booking = await getBookingById(bookingId);
        if ((booking as any)?.addonPaymentStatus === 'PAID') {
          setAddonPaymentPhase('done');
          showBookedToastAndNavigate();
          return;
        }
      } catch (err) {
        // Transient poll failure — keep trying until the timeout below.
      }

      if (addonPollCancelRef.current) return;
      if (Date.now() - startedAt >= 30000) {
        setAddonPaymentError(
          t(
            'credit.addon_confirmation_pending',
            "We're still confirming your add-on payment. You can check My Packages shortly."
          )
        );
        setAddonPaymentPhase('idle');
        return;
      }
      setTimeout(pollOnce, 1500);
    };

    pollOnce();
  };

  const handleAddonAuthorized = () => {
    if (createdBooking?.bookingId) {
      setAddonPaymentPhase('confirming');
      pollForAddonPaymentResolution(createdBooking.bookingId);
    }
  };

  const handleConfirm = () => {
    if (!slot?.date || !slot?.timeSlotId) return;
    const primaryAddress = user?.address?.[0];
    const personalInfo = {
      firstName: user?.firstName,
      lastName: user?.lastName,
      email: user?.email,
      phone: user?.mobile || user?.phone || user?.phoneNumber,
      address: primaryAddress
        ? {
            street: primaryAddress.addressLine1,
            city: primaryAddress.city,
            state: primaryAddress.state,
            postalCode: primaryAddress.postalCode,
          }
        : undefined,
    };
    bookSessionMutation.mutate(
      {
        customerPackageId: matchingPackage.customerPackageId,
        data: {
          providerId: service.providerId,
          serviceId: service.serviceId,
          appointmentDate: slot.date,
          appointmentTimeFrom: slot.start,
          appointmentTimeTo: slot.end,
          staffId: slot.staffId,
          personalInfo,
          note: undefined,
          // Only send extras. If none, send undefined so the backend
          // doesn't get an empty array (and no charge is implied).
          additionalServiceIds:
            selectedAdditionalServiceIds.length > 0
              ? selectedAdditionalServiceIds
              : undefined,
        },
      },
      {
        onSuccess: ({ booking }) => {
          // Close the review modal now that the booking exists.
          setShowBookingCheckout(false);
          const addonSubtotal = Number(booking?.addonSubtotal) || 0;
          if (addonSubtotal > 0 && booking?.bookingId) {
            setCreatedBooking(booking);
            setAddonPaymentPhase('paying');
            return;
          }
          showBookedToastAndNavigate();
        },
        // onError: leave the modal open so the user can retry.
      }
    );
  };

  // Confirm Booking click: always show the checkout modal first so the
  // customer reviews what they're booking (credits + any paid extras)
  // before we hit the API. The modal stays open until the mutation
  // succeeds (see the onSuccess in handleConfirm below).
  const handleConfirmClick = () => {
    if (!slot?.date || !slot?.timeSlotId) return;
    setShowBookingCheckout(true);
  };

  // Only worth asking when there's an actual choice — DateAndStaff already
  // auto-selects the sole staff member internally either way (its own
  // effect sets selectedStaffId regardless of whether this picker renders),
  // so hiding it here is a density decision only, never a functional one.
  const staffCount = dateAndStaffState?.availableStaff?.length ?? 0;
  const showStaffPicker = staffCount > 1;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <button
        onClick={() => navigate(all_routes.customerPackages, { replace: true })}
        className="flex items-center gap-2 text-body text-gray-500 hover:text-gray-800 mb-3 cursor-pointer"
      >
        <FaArrowLeft size={14} /> {t('credit.back', 'Back to My Packages')}
      </button>

      <div className="mb-4">
        <h1 className="text-title2 font-bold text-gray-900">
          {t('credit.book_title', 'Book an Appointment')}
        </h1>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 items-start">
        {/* ===== LEFT: Calendar ===== */}
        <div className="w-full lg:w-auto lg:shrink-0">
          <DateAndStaff
            data={service}
            onChange={(sel: any) =>
              setSlot({
                date: sel.date,
                timeSlotId: sel.timeSlotId,
                staffId: sel.staffId,
                start: sel.start,
                end: sel.end,
              })
            }
            onStateChange={setDateAndStaffState}
          />
        </div>

        {/* ===== RIGHT: Summary, time, staff, confirm ===== */}
        <div className="w-full lg:flex-1 min-w-0 flex flex-col gap-3">
          {/* Package summary — same information MyPackages.tsx's card shows
              (service image, name, provider, tier, sessions remaining,
              expiry) condensed to one row so a customer with multiple
              packages can still tell which one they're booking against,
              without it costing a second stacked card's worth of height. */}
          <Card shadow="sm" className="border border-gray-200 overflow-hidden">
            <div className="flex items-center gap-3 p-3">
              <div className="relative w-14 h-14 bg-gray-200 overflow-hidden shrink-0 rounded-lg">
                <Image
                  src={
                    service.gallery?.[0]?.serviceImages?.[0] || fallbackImage
                  }
                  alt={service.serviceTitle || matchingPackage.tier?.name}
                  className="object-cover w-full h-full"
                  shadow="none"
                  radius="none"
                  removeWrapper={true}
                />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-body-bold text-gray-800 truncate">
                  {service.serviceTitle}
                </p>
                <p className="text-caption text-gray-500 truncate">
                  {matchingPackage.tier?.name}
                  {' · '}
                  {/* Same fallback chain MyPackages.tsx uses, with one
                      addition: `service` here is a direct single-service
                      fetch (not the per-package batch lookup My Packages
                      does), so its own businessName/providerName are
                      reliable today and don't block on pms populating
                      matchingPackage.provider — that field is still checked
                      last, for full consistency once it lands. */}
                  {service.providerName ||
                    service.businessName ||
                    matchingPackage.provider?.name ||
                    'N/A'}
                </p>
              </div>
              <div className="flex flex-col items-end gap-0.5 shrink-0 pl-3 border-l border-gray-100">
                <p className="text-body-bold text-gray-800 whitespace-nowrap">
                  {matchingPackage.sessionsRemaining}/
                  {matchingPackage.sessionsTotal}{' '}
                  {t('credit.credits_left', 'appointments left')}
                </p>
                <p className="text-caption text-gray-500 flex items-center gap-1 whitespace-nowrap">
                  <FaCalendarDay size={11} className="text-gray-400" />
                  {t('packages.expires', 'Expires')}:{' '}
                  {formatDate(matchingPackage.expiresAt)}
                </p>
              </div>
            </div>
          </Card>

          {/* Included in Your Package — add-ons already paid for at
              purchase/offer time and assigned to THIS specific session
              number. Read-only: nothing to select, no extra charge. Shown
              separately from, and above, the still-purchasable extras below
              so the customer can tell at a glance what's already theirs for
              this appointment vs what would be a new charge. */}
          {includedAddOns.length > 0 && (
            <Card shadow="sm" className="border border-gray-200">
              <CardHeader className="border-b border-gray-100 px-4 py-3 flex items-center justify-between gap-2">
                <p className="text-subtitle3 font-semibold text-gray-800">
                  {t('credit.included_addons', 'Included in Your Package')}
                </p>
                <Chip size="sm" radius="full" color="success" variant="flat">
                  {t('credit.for_session', 'Appointment')}{' '}
                  {currentSessionNumber}
                </Chip>
              </CardHeader>
              <CardBody className="px-4 py-3">
                <AddOnSelector
                  addons={includedAddOns}
                  selectedIds={includedAddOns.map((a) => a.id)}
                  onChange={() => {}}
                  readOnly
                />
              </CardBody>
            </Card>
          )}

          {/* Optional Add-ons — everything else the service offers. Selecting
              one here is a brand-new purchase, charged in addition to the
              package credit this booking redeems. */}
          {extraAddOns.length > 0 && (
            <Card shadow="sm" className="border border-gray-200">
              <CardHeader className="border-b border-gray-100 px-4 py-3 flex items-center gap-2">
                <p className="text-subtitle3 font-semibold text-gray-800">
                  {t('credit.optional_addons', 'Optional Add-ons')}
                </p>
              </CardHeader>
              <CardBody className="px-4 py-3">
                <AddOnSelector
                  addons={extraAddOns}
                  selectedIds={selectedAdditionalServiceIds}
                  onChange={setSelectedAdditionalServiceIds}
                />
              </CardBody>
            </Card>
          )}

          <Card shadow="sm" className="border border-gray-200">
            <CardHeader className="border-b border-gray-100 px-4 py-3 flex items-center justify-between gap-2">
              <p className="text-subtitle3 font-semibold text-gray-800">
                {t('credit.session_details', 'Appointment details')}
              </p>
              <Chip size="sm" radius="full" color="secondary" variant="flat" className="text-xs">
                {t('credit.uses_one', 'Uses 1 appointment')}
              </Chip>
            </CardHeader>
            <CardBody className="px-4 py-3 flex flex-col gap-3">
              {/* Time slots — DateAndStaff fetches these itself but never
                  renders them; the parent must (BookingPage.tsx is the
                  reference pattern this mirrors). Without this the calendar
                  showed no time slots at all while DateAndStaff silently
                  auto-picked the first slot and an auto-derived staff member
                  behind the scenes. Compact grid (more columns, tighter
                  padding) instead of full-width pills — same pill styling,
                  just denser packing. */}
              <div>
                <p className="text-body-bold mb-2">
                  {t('booking.appointment_time', 'Appointment Time')}
                </p>
                {!dateAndStaffState ? (
                  <p className="text-caption">
                    {t('booking.choose_date_time', 'Choose a date first')}
                  </p>
                ) : dateAndStaffState.isPending ? (
                  <div className="w-full flex justify-center items-center py-2">
                    <SmallLoadingSpinner size="sm" />
                  </div>
                ) : dateAndStaffState.isCheckTimeSlotError ? (
                  <p className="text-xs text-red-400">
                    {dateAndStaffState.checkTimeSlotError}
                  </p>
                ) : dateAndStaffState.timeSlots.length === 0 ? (
                  <p className="text-caption">
                    {dateAndStaffState.availabilityMessage ||
                      t('booking.no_available_times', 'No available times')}
                  </p>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5 max-h-[190px] overflow-y-auto">
                    {dateAndStaffState.timeSlots.map(
                      (time: any, index: number) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() =>
                            dateAndStaffState.setSelectTimeSlot(
                              time?.timeSlotId
                            )
                          }
                          className={`flex justify-center items-center px-2 py-1.5 rounded-full border font-medium transition whitespace-nowrap ${
                            dateAndStaffState.selectTimeSlot ===
                            time?.timeSlotId
                              ? 'border-secondary bg-secondary text-white'
                              : 'border-gray-200 bg-white hover-primary-gradient'
                          }`}
                        >
                          <p
                            className={`text-xs ${
                              dateAndStaffState.selectTimeSlot ===
                              time?.timeSlotId
                                ? 'text-white'
                                : 'text-gray-700'
                            }`}
                          >{`${time?.from} - ${time?.to}`}</p>
                        </button>
                      )
                    )}
                  </div>
                )}
              </div>

              {/* Staff — same rationale as the time-slot block above. Skipped
                  entirely when there's nothing to actually choose between
                  (0 or 1 available staff, or no time slot picked yet);
                  DateAndStaff already auto-selects the sole match either
                  way, so this is purely a display decision. */}
              {dateAndStaffState &&
                !dateAndStaffState.isPending &&
                dateAndStaffState.timeSlots.length > 0 &&
                (staffCount === 0 ? (
                  <div className="pt-1 border-t border-gray-100">
                    <p className="text-body-bold mb-2">
                      {t('booking.select_staff', 'Select Staff')}
                    </p>
                    <p className="text-caption">
                      {dateAndStaffState.availabilityMessage ||
                        t('booking.no_available_staff', 'No available staff')}
                    </p>
                  </div>
                ) : (
                  showStaffPicker && (
                    <div className="pt-1 border-t border-gray-100">
                      <p className="text-body-bold mb-2">
                        {t('booking.select_staff', 'Select Staff')}
                      </p>
                      <div className="grid grid-cols-3 gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            dateAndStaffState.setSelectedStaffId(ANY_STAFF_ID)
                          }
                          className={`flex text-xs font-medium flex-col items-center gap-1 border rounded-xl px-2 py-2 transition ${
                            dateAndStaffState.selectedStaffId === ANY_STAFF_ID
                              ? 'border-secondary bg-secondary/10'
                              : 'border-gray-200 bg-white hover-primary-gradient'
                          }`}
                        >
                          {ANY_STAFF_LABEL}
                        </button>
                        {dateAndStaffState.availableStaff?.map(
                          (s: any, index: number) => {
                            const isSelected =
                              dateAndStaffState.selectedStaffId === s?.staffId;
                            return (
                              <button
                                key={index}
                                type="button"
                                onClick={() =>
                                  dateAndStaffState.setSelectedStaffId(
                                    s?.staffId || ''
                                  )
                                }
                                className={`flex text-xs font-medium flex-col items-center gap-1 border rounded-xl px-2 py-2 transition ${
                                  isSelected
                                    ? 'border-secondary bg-secondary/10'
                                    : 'border-gray-200 bg-white hover-primary-gradient'
                                }`}
                              >
                                {s?.fullName}
                              </button>
                            );
                          }
                        )}
                      </div>
                    </div>
                  )
                ))}
            </CardBody>

            <CardFooter className="px-4 py-3 flex flex-col gap-2 border-t border-gray-100">
              {slot?.time && (
                <div className="flex items-center gap-2 text-body text-gray-700">
                  <FaCalendarCheck size={16} className="text-primary" />
                  <span>
                    {slot.date} · {slot.time}
                  </span>
                </div>
              )}
              <CustomButton
                label={t('credit.confirm', 'Confirm Booking')}
                color="primary"
                size="md"
                fullWidth
                startContent={<FaCircleCheck />}
                isLoading={bookSessionMutation.isPending}
                isDisabled={
                  !slot?.date ||
                  !slot?.timeSlotId ||
                  bookSessionMutation.isPending
                }
                onPress={handleConfirmClick}
              />
              <p className="text-center text-caption text-gray-400">
                {t(
                  'credit.no_payment',
                  'No payment required — this appointment uses 1 appointment from your package.'
                )}
              </p>
            </CardFooter>
          </Card>

          {bookSessionMutation.isError && (
            <CustomAlert
              title={t('credit.booking_error_title', 'Booking failed')}
              description={
                (bookSessionMutation.error as any)?.message ||
                t(
                  'credit.booking_error_desc',
                  'We could not book your appointment. Please try again.'
                )
              }
              color="danger"
            />
          )}

          {/* Phase 4 — appointment-time add-on payment. Shown once
              bookSession succeeds with a chargeable addonSubtotal; the
              appointment itself is already booked at this point (credit
              already spent) — this only settles the extra add-ons. */}
          {addonPaymentPhase !== 'idle' && createdBooking && (
            <Card shadow="sm" className="border border-gray-200">
              <CardHeader className="border-b border-gray-100 px-4 py-3">
                <p className="text-subtitle3 font-semibold text-gray-800">
                  {t('credit.pay_addons_title', 'Pay for Add-ons')}
                </p>
              </CardHeader>
              <CardBody className="px-4 py-3 flex flex-col gap-3">
                {addonPaymentPhase === 'paying' &&
                  onlineAddonPaymentAvailable && (
                    <PaymentSection
                      paymentType="package_session_addon"
                      bookingId={createdBooking.bookingId}
                      serviceId={service.serviceId}
                      providerId={service.providerId}
                      amount={Number(createdBooking.addonSubtotal) || 0}
                      returnUrl={`${window.location.origin}${all_routes.customerPackages}`}
                      onAuthorized={handleAddonAuthorized}
                    />
                  )}
                {addonPaymentPhase === 'paying' &&
                  !onlineAddonPaymentAvailable && (
                    <p className="text-caption text-gray-500">
                      {t(
                        'credit.addon_offline_only',
                        'Online payment is unavailable right now — settle this add-on directly with the provider.'
                      )}
                    </p>
                  )}
                {addonPaymentPhase === 'confirming' && (
                  <div className="flex flex-col items-center gap-2 py-2">
                    <SmallLoadingSpinner size="sm" />
                    <p className="text-caption text-gray-500">
                      {t(
                        'credit.confirming_addon_payment',
                        'Confirming your add-on payment…'
                      )}
                    </p>
                  </div>
                )}
                {addonPaymentError && (
                  <p className="text-xs text-red-500" role="alert">
                    {addonPaymentError}
                  </p>
                )}
                {addonPaymentPhase === 'paying' && (
                  <CustomButton
                    label={t('credit.pay_in_person', "I'll pay in person")}
                    color="default"
                    variant="flat"
                    size="sm"
                    onPress={showBookedToastAndNavigate}
                  />
                )}
              </CardBody>
            </Card>
          )}
        </div>
      </div>

      {/* Add-ons confirmation checkout — only shown when the customer has
          selected at least one chargeable EXTRA add-on. Package-included
          add-ons never trigger this since they cost nothing at booking time. */}
      <BookingCheckoutModal
        open={showBookingCheckout}
        onOpenChange={(open) => {
          if (bookSessionMutation.isPending) return;
          setShowBookingCheckout(open);
        }}
        addOns={selectedAddOnDetails}
        isLoading={bookSessionMutation.isPending}
        onConfirm={handleConfirm}
      />
    </div>
  );
};

export default CreditSession;
